import React, { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import { getOrganization, updateOrganization } from '../api/api';
import { useAuth } from '../context/AuthContext';

const CARD = { borderColor: '#f4d6df', background: '#fff' };
const PINK = '#c44569';

function Field({ label, disabled, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">{label}</span>
      <input
        {...rest}
        disabled={disabled}
        className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200 disabled:bg-slate-50 disabled:text-slate-400"
        style={{ borderColor: '#f4d6df' }}
      />
    </label>
  );
}

function Banner({ tone, text }) {
  const s = tone === 'success'
    ? { borderColor: '#a7f3d0', background: '#ecfdf5', color: '#047857' }
    : { borderColor: '#f9b8ca', background: '#fce0e8', color: PINK };
  return <div className="rounded-2xl border px-4 py-3 text-sm" style={s}>{text}</div>;
}

export default function OrgAdminSettingsPage() {
  const { activeOrg, canManageOrganizationSettings } = useAuth();
  const [form, setForm] = useState({ name: '', industry: '', email: '', phone: '', country: '', currency: 'USD', timezone: 'UTC' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!activeOrg?.id) return;
    setLoading(true);
    getOrganization(activeOrg.id)
      .then((res) => {
        const org = res?.data?.data?.organization || {};
        setForm({
          name: org.name || '',
          industry: org.industry || '',
          email: org.email || '',
          phone: org.phone || '',
          country: org.country || '',
          currency: org.currency || 'USD',
          timezone: org.timezone || 'UTC',
        });
      })
      .catch((e) => setError(e?.response?.data?.message || 'Failed to load settings.'))
      .finally(() => setLoading(false));
  }, [activeOrg?.id]);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canManageOrganizationSettings) return;
    setSaving(true); setMessage(''); setError('');
    try {
      await updateOrganization(activeOrg.id, form);
      setMessage('Organization settings updated successfully.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update settings.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pb-10 max-w-3xl">
      <div className="rounded-3xl border p-5 shadow-sm" style={CARD}>
        <div className="flex items-center gap-3">
          <Settings size={20} style={{ color: PINK }} />
          <div>
            <h1 className="text-xl font-black text-slate-900">Organization Settings</h1>
            <p className="text-sm text-slate-500 mt-0.5">Update tenant profile, regional defaults, and operational details.</p>
          </div>
        </div>
      </div>

      {message ? <Banner tone="success" text={message} /> : null}
      {error ? <Banner tone="error" text={error} /> : null}

      <form onSubmit={onSubmit} className="rounded-3xl border p-5 shadow-sm space-y-4" style={CARD}>
        {loading ? <p className="text-sm text-slate-400 py-4 text-center">Loading settings…</p> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Organization Name" name="name"     type="text"  value={form.name}     onChange={onChange} disabled={!canManageOrganizationSettings} />
              <Field label="Industry"          name="industry" type="text"  value={form.industry} onChange={onChange} disabled={!canManageOrganizationSettings} />
              <Field label="Support Email"     name="email"    type="email" value={form.email}    onChange={onChange} disabled={!canManageOrganizationSettings} />
              <Field label="Phone"             name="phone"    type="text"  value={form.phone}    onChange={onChange} disabled={!canManageOrganizationSettings} />
              <Field label="Country"           name="country"  type="text"  value={form.country}  onChange={onChange} disabled={!canManageOrganizationSettings} />
              <Field label="Currency"          name="currency" type="text"  value={form.currency} onChange={onChange} disabled={!canManageOrganizationSettings} />
              <div className="md:col-span-2">
                <Field label="Timezone" name="timezone" type="text" value={form.timezone} onChange={onChange} disabled={!canManageOrganizationSettings} />
              </div>
            </div>
            <div className="pt-2 flex items-center gap-4">
              <button
                type="submit"
                disabled={!canManageOrganizationSettings || saving}
                className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90 transition"
                style={{ background: PINK }}
              >
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              {!canManageOrganizationSettings ? (
                <p className="text-xs text-slate-400">Read-only. Contact your Organization Head to update settings.</p>
              ) : null}
            </div>
          </>
        )}
      </form>
    </div>
  );
}
